import { useMemo } from 'react';

import { useCoreValues } from '../api/use-core-values';

export interface CoreValuesPrefill {
  readonly price: number | null;
  readonly weightMt: number | null;
}

const EMPTY: CoreValuesPrefill = { price: null, weightMt: null };

// The side's current expectation from the value log — the base the create
// dialogs prefill price/weight from, so the figures the operator edits match
// the Initial/Provisional/Final grid. Null fields keep the ticket/BL guess.
// Memoized so the dialogs' defaults (and their open-reset effects) don't
// recompute on every render.
export function useCoreValuesProvisionalPrefill(
  blOrderId: string,
  side: 'buy' | 'sell',
): CoreValuesPrefill {
  const direction = side === 'buy' ? 'Purchase' : 'Sale';
  const { data } = useCoreValues(blOrderId);
  return useMemo(() => {
    const state = data?.sides.find((entry) => entry.side === direction);
    if (state === undefined) return EMPTY;
    return {
      price: toFinite(state.price.provisional),
      weightMt: toFinite(state.weight_mt.provisional),
    };
  }, [data, direction]);
}

function toFinite(value: string | null): number | null {
  if (value === null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

// Overrides the guessed price/weight with the log's expectation and recomputes
// the total off the same formula the base defaults use (gross minus the
// deductions the dialog nets). Only meaningful for fresh invoices — callers
// skip it when editing an existing row.
export function applyCoreValuesPrefill<
  T extends {
    market_reference_price?: number;
    amount_qt_mt?: number;
    total_amount?: number;
    applied_downpayment_amount?: number;
  },
>(defaults: T, log: CoreValuesPrefill, netOfProvisional = 0): T {
  if (log.price === null && log.weightMt === null) return defaults;
  const price = log.price ?? numberOrZero(defaults.market_reference_price);
  const weightMt = log.weightMt ?? numberOrZero(defaults.amount_qt_mt);
  const downpayment = numberOrZero(defaults.applied_downpayment_amount);
  return {
    ...defaults,
    market_reference_price: price,
    amount_qt_mt: weightMt,
    total_amount: Math.max(price * weightMt - downpayment - netOfProvisional, 0),
  };
}

function numberOrZero(value: number | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}
